import { coverageCellKey, type AdminRegistryApp, type RoleMapCoverage } from '@/api/console';
import type { MatrixCell } from './types';
import type { RoleKeyByKey } from './types';
import shared from './coverageShared.module.css';

/**
 * EAA-1817: pure derivations for the **Ma trận** view (design frame 55a) — the
 * standard-role × application grid.
 *
 * WHY THIS IS A NEW FILE AND NOT `coverageHelpers.ts`. That file rolls the
 * coverage response up PER APPLICATION for the left rail: one row per app, the
 * role axis collapsed into counts. Frame 55a needs the opposite orientation —
 * one row per standard role, the app axis kept as real columns — plus tallies
 * the rail has no use for (cells, not apps). Sharing one file would mean two
 * sets of functions that read the same input and answer opposite questions.
 *
 * AXIS ORIENTATION IS PART OF THE SPEC, not an implementation detail. Frame 55a
 * draws **roles down, apps across**, and the deleted `CoverageMatrixCard` drew
 * apps down, roles across. That older orientation came from a design revision
 * the design's own changelog reverted on 2026-08-11 ("Bỏ ma trận, xem độ phủ
 * bằng bảng chuẩn"); the matrix returned on 2026-08-24 transposed, because the
 * question it answers is "which of MY standard roles is not landing anywhere",
 * and a standard role is the thing an admin owns. Do not transpose it back.
 *
 * Nothing here recomputes a cell's health: `CoverageCellState` is resolved
 * server-side (paused wins over everything — see `RoleMapCoverage`'s docstring)
 * and this file only tallies what the server already decided.
 */

/** One application column of the matrix. */
export interface MatrixAppColumn {
    code: string;
    name: string;
    /**
     * `Application.enabled` — the approval flag. A not-yet-approved app cannot
     * be linked at all, so frame 55a greys its whole column and prints "chưa
     * sẵn sàng" where the other columns print their `n/m`. It is NOT a
     * heartbeat; see `PLAN-EAA-1817`'s note on the "Đang chạy" label.
     */
    ready: boolean;
    /** Standard roles this app has at least one non-`none` cell for. */
    coveredCount: number;
    /** Always the full role-key count — the same denominator for every column. */
    totalRoleKeys: number;
}

/** One standard-role row of the matrix. */
export interface MatrixRoleRow {
    roleKey: string;
    /** Vietnamese display name — the row's PRIMARY line (see frame 55a). */
    roleName: string;
    /** Ready apps this role is linked into. */
    coveredCount: number;
    /**
     * Denominator for the row's `n/m` bar: the number of **ready** apps, not
     * every app. Frame 55a shows `4/7` while drawing 8 columns, because the
     * eighth is the not-ready one — counting a column no one can link into
     * would make a fully-linked role look permanently short.
     */
    readyAppCount: number;
    /** Cells in this row, one per app column, in `columns` order. */
    cells: MatrixCell[];
    /** `true` when any cell in the row is `paused` — the row's error styling. */
    hasPaused: boolean;
    /** `true` when any READY app's cell is `none` — drives the "chưa phủ" chip. */
    hasUncovered: boolean;
}

/** Whole-grid tallies for the toolbar chips and the table footer. */
export interface MatrixTallies {
    /** Cells that can actually hold a link: rows × READY columns. */
    cellCount: number;
    linkedCount: number;
    uncoveredCount: number;
    pausedCount: number;
}

/** Columns, in the registry's own order, each with its coverage rollup. */
export function buildMatrixColumns(
    apps: AdminRegistryApp[],
    coverage: RoleMapCoverage
): MatrixAppColumn[] {
    const totalRoleKeys = coverage.role_keys.length;
    return apps.map((app) => {
        let coveredCount = 0;
        for (const roleKey of coverage.role_keys) {
            const cell = coverage.cells[coverageCellKey(app.code, roleKey)];
            if (cell && cell.state !== 'none') coveredCount += 1;
        }
        return {
            code: app.code,
            name: app.name,
            ready: app.enabled,
            coveredCount,
            totalRoleKeys,
        };
    });
}

/**
 * Rows, in the coverage endpoint's own `role_keys` order, each carrying one
 * cell per column.
 *
 * `roleKeyByKey` supplies the Vietnamese name. A role key with no vocabulary
 * row falls back to the key itself rather than rendering an empty primary
 * line — the coverage endpoint and `/role-keys` are two queries, so they can
 * legitimately disagree for one render after a role is added.
 */
export function buildMatrixRows(
    columns: MatrixAppColumn[],
    coverage: RoleMapCoverage,
    roleKeyByKey: RoleKeyByKey
): MatrixRoleRow[] {
    const readyAppCount = columns.filter((column) => column.ready).length;

    return coverage.role_keys.map((roleKey) => {
        const cells = columns.map((column): MatrixCell => {
            const cell = coverage.cells[coverageCellKey(column.code, roleKey)];
            return {
                appCode: column.code,
                roleKey,
                state: cell?.state ?? 'none',
                count: cell?.count ?? 0,
                roles: cell?.roles ?? [],
                roleNames: cell?.role_names ?? [],
            };
        });

        let coveredCount = 0;
        let hasPaused = false;
        let hasUncovered = false;
        for (let i = 0; i < cells.length; i += 1) {
            // A not-ready app contributes to neither numerator nor defect
            // flags: its cells are drawn as "—", not as a gap to go and fix.
            if (!columns[i].ready) continue;
            const state = cells[i].state;
            if (state === 'none') {
                hasUncovered = true;
                continue;
            }
            coveredCount += 1;
            if (state === 'paused') hasPaused = true;
        }

        return {
            roleKey,
            roleName: roleKeyByKey.get(roleKey)?.name ?? roleKey,
            coveredCount,
            readyAppCount,
            cells,
            hasPaused,
            hasUncovered,
        };
    });
}

/** Grid-wide counts over READY columns only — same reasoning as the row flags. */
export function computeMatrixTallies(
    columns: MatrixAppColumn[],
    rows: MatrixRoleRow[]
): MatrixTallies {
    let linkedCount = 0;
    let uncoveredCount = 0;
    let pausedCount = 0;
    for (const row of rows) {
        for (let i = 0; i < row.cells.length; i += 1) {
            if (!columns[i].ready) continue;
            const state = row.cells[i].state;
            if (state === 'none') uncoveredCount += 1;
            else if (state === 'paused') pausedCount += 1;
            else linkedCount += 1;
        }
    }
    const readyAppCount = columns.filter((column) => column.ready).length;
    return {
        cellCount: rows.length * readyAppCount,
        linkedCount,
        uncoveredCount,
        pausedCount,
    };
}

/** Coverage-bar tone, matching the rail's own thresholds so one app reads the
 *  same in both places (`coverageShared.module.css`'s `.progressFill*`). */
export type CoverageBarTone = 'empty' | 'ok' | 'warn';

export function coverageBarTone(covered: number, total: number): CoverageBarTone {
    if (covered === 0) return 'empty';
    if (total > 0 && covered / total >= 0.5) return 'ok';
    return 'warn';
}

// EAA-1846: the tone modifier used to be built as `rc-app-row-progress-fill--${tone}`.
// A CSS-Module name is hashed at build time, so a template literal cannot name
// one — the lookup has to be explicit. It lives beside `coverageBarTone` so the
// tone and its paint stay one decision for both rails and the matrix.
export const COVERAGE_BAR_TONE_CLASS: Record<CoverageBarTone, string> = {
    empty: shared.progressFillEmpty,
    ok: shared.progressFillOk,
    warn: shared.progressFillWarn,
};
